import React, { useEffect, useState } from 'react';
import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';

function tsToMs(ts: any): number {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return 0;
}

function fmtDate(ts: any): string {
  const ms = tsToMs(ts);
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface CoachRow {
  id: string;
  name: string;
  email: string;
  status: 'active' | 'inactive';
  createdAt: any;
}

const BLANK_FORM = { name: '', email: '' };

export function FirmCoaches() {
  const { firmAdmin } = useAuth();
  const { firmId } = firmAdmin!;

  const [coaches, setCoaches] = useState<CoachRow[]>([]);
  const [enrolledCounts, setEnrolledCounts] = useState<Record<string, { total: number; approved: number; pending: number }>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Add coach form
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(BLANK_FORM);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');

  // Per-row toggle state
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Edit coach modal
  const [editingCoach, setEditingCoach] = useState<CoachRow | null>(null);
  const [editNameDraft, setEditNameDraft] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');

  useEffect(() => { loadData(); }, [firmId]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [coachesSnap, profilesSnap] = await Promise.all([
        getDocs(query(collection(db, 'coaches'), where('firmId', '==', firmId))),
        getDocs(query(collection(db, 'profiles'), where('firmId', '==', firmId))),
      ]);

      const counts: Record<string, { total: number; approved: number; pending: number }> = {};
      for (const d of profilesSnap.docs) {
        const p = d.data();
        const cid = p.coachId;
        if (!cid) continue;
        if (!counts[cid]) counts[cid] = { total: 0, approved: 0, pending: 0 };
        counts[cid].total++;
        if (p.enrollmentStatus === 'approved') counts[cid].approved++;
        if (p.enrollmentStatus === 'pending') counts[cid].pending++;
      }
      setEnrolledCounts(counts);

      setCoaches(
        coachesSnap.docs.map(d => ({
          id: d.id,
          name: d.data().name ?? '',
          email: d.data().email ?? '',
          status: d.data().status ?? 'inactive',
          createdAt: d.data().createdAt,
        }))
      );
    } catch (e: any) {
      setError(e.message || 'Failed to load coaches.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = addForm.name.trim();
    const email = addForm.email.trim().toLowerCase();
    if (!name || !email.includes('@')) return;
    setAdding(true);
    setAddError('');
    try {
      // Doc ID = email (lowercase). Consistent with the auth resolver's email-based fallback.
      await setDoc(doc(db, 'coaches', email), {
        firmId,
        name,
        email,
        status: 'active',
        createdAt: serverTimestamp(),
      });
      setAddForm(BLANK_FORM);
      setAddOpen(false);
      await loadData();
    } catch (e: any) {
      setAddError(e.message || 'Failed to add coach.');
    } finally {
      setAdding(false);
    }
  };

  const openEdit = (coach: CoachRow) => {
    setEditingCoach(coach);
    setEditNameDraft(coach.name);
    setEditError('');
  };

  const handleSaveEdit = async () => {
    const name = editNameDraft.trim();
    if (!name || !editingCoach) return;
    setSavingEdit(true);
    setEditError('');
    try {
      await updateDoc(doc(db, 'coaches', editingCoach.id), { name });
      setCoaches(prev => prev.map(c => c.id === editingCoach.id ? { ...c, name } : c));
      setEditingCoach(null);
    } catch (e: any) {
      setEditError(e.message || 'Failed to save.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleToggle = async (coach: CoachRow) => {
    const newStatus = coach.status === 'active' ? 'inactive' : 'active';
    setTogglingId(coach.id);
    try {
      await updateDoc(doc(db, 'coaches', coach.id), { status: newStatus });
      setCoaches(prev =>
        prev.map(c => c.id === coach.id ? { ...c, status: newStatus } : c)
      );
    } catch (e: any) {
      setError(e.message || 'Failed to update coach status.');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <Layout>
      <div style={{ maxWidth: 900 }}>
        <div className="page-header">
          <h1 className="page-title">Coaches</h1>
          <button
            className="btn-primary"
            onClick={() => { setAddOpen(o => !o); setAddError(''); setAddForm(BLANK_FORM); }}
            style={{ padding: '8px 18px', fontSize: 13 }}
          >
            {addOpen ? 'Cancel' : '+ Add Coach'}
          </button>
        </div>

        {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

        {/* Add coach form */}
        {addOpen && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
              Add Coach
            </div>
            {addError && <div className="error-message" style={{ marginBottom: 14 }}>{addError}</div>}
            <form onSubmit={handleAdd}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input
                    className="form-input"
                    required
                    value={addForm.name}
                    onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Full name"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input
                    className="form-input"
                    type="email"
                    required
                    value={addForm.email}
                    onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="coach@email.com"
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" className="btn-primary" disabled={adding}>
                  {adding ? 'Adding…' : 'Add Coach'}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => { setAddOpen(false); setAddError(''); }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="card" style={{ padding: 0 }}>
          {loading ? (
            <div className="loading">Loading…</div>
          ) : coaches.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14 }}>
              No coaches added yet.
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th style={{ textAlign: 'right' }}>Approved</th>
                    <th style={{ textAlign: 'right' }}>Pending</th>
                    <th>Added</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {coaches.map(c => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 500 }}>{c.name || '—'}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--text-secondary)' }}>
                        {c.email}
                      </td>
                      <td>
                        <span className={`badge badge-${c.status === 'active' ? 'active' : 'inactive'}`}>
                          {c.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right', fontSize: 13, fontWeight: 500 }}>
                        {enrolledCounts[c.id]?.total ?? 0}
                      </td>
                      <td style={{ textAlign: 'right', fontSize: 13, color: 'var(--accent-text)' }}>
                        {enrolledCounts[c.id]?.approved ?? 0}
                      </td>
                      <td style={{ textAlign: 'right', fontSize: 13, color: 'var(--warning)' }}>
                        {enrolledCounts[c.id]?.pending ?? 0}
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        {fmtDate(c.createdAt)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', alignItems: 'center' }}>
                          <button
                            className="btn-secondary"
                            style={{ padding: '3px 10px', fontSize: 12 }}
                            onClick={() => openEdit(c)}
                          >
                            Edit
                          </button>
                          <label className="toggle-switch" title={c.status === 'active' ? 'Deactivate' : 'Activate'}>
                            <div
                              className={`toggle-track${c.status === 'active' ? ' on' : ''}`}
                              style={{ opacity: togglingId === c.id ? 0.5 : 1 }}
                              onClick={() => { if (!togglingId) handleToggle(c); }}
                            >
                              <div className="toggle-thumb" />
                            </div>
                            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                              {c.status === 'active' ? 'Active' : 'Inactive'}
                            </span>
                          </label>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
          {coaches.filter(c => c.status === 'active').length} active ·{' '}
          {coaches.filter(c => c.status === 'inactive').length} inactive
        </div>
      </div>
      {editingCoach && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'var(--overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={e => { if (e.target === e.currentTarget) setEditingCoach(null); }}
        >
          <div className="card" style={{ maxWidth: 480, width: '90%' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
              Edit Coach
            </div>
            {editError && <div className="error-message" style={{ marginBottom: 14 }}>{editError}</div>}
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Name *</label>
              <input
                className="form-input"
                value={editNameDraft}
                onChange={e => setEditNameDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') setEditingCoach(null); }}
                autoFocus
              />
            </div>
            <div className="form-group" style={{ marginBottom: 24 }}>
              <label className="form-label">Email</label>
              <input
                className="form-input"
                value={editingCoach.email}
                readOnly
                style={{ opacity: 0.6, cursor: 'not-allowed' }}
              />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                Email cannot be changed after creation. To correct a wrong email, deactivate this coach and create a new record.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                className="btn-primary"
                onClick={handleSaveEdit}
                disabled={savingEdit || !editNameDraft.trim()}
              >
                {savingEdit ? 'Saving…' : 'Save'}
              </button>
              <button
                className="btn-secondary"
                onClick={() => setEditingCoach(null)}
                disabled={savingEdit}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
